
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function PassengerProfile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [chatRequested, setChatRequested] = useState(false);
  
  // Mock passenger data - in real app this would come from API
  const [passenger] = useState({
    id: 1,
    name: 'سارة أحمد',
    avatar: 'https://public.readdy.ai/ai/img_res/f3ea377860f3fbe0e07a5264d8477317.jpg',
    bio: 'مسافرة سياحية - الرياض إلى دبي',
    gender: 'female',
    isOnline: true,
    country: 'السعودية',
    destination: 'دبي',
    age: 28,
    profession: 'مصممة جرافيك',
    interests: ['السفر', 'التصوير', 'القراءة', 'الطبخ'],
    languages: ['العربية', 'الإنجليزية'],
    flightInfo: {
      flightNumber: 'SV123',
      departure: 'الرياض',
      arrival: 'دبي',
      date: '2024-01-15',
      time: '14:30',
      seat: '12A'
    },
    verified: true,
    rating: 4.8,
    reviewsCount: 24,
    showContact: true,
    phone: '+966501234567',
    email: 'sara.ahmed@email.com'
  });

  const handleChatRequest = () => {
    setChatRequested(true);
    // In real app, this would send a chat request to the passenger
    setTimeout(() => {
      navigate('/chats');
    }, 2000);
  };

  const handleCall = () => {
    if (passenger.phone) {
      window.open(`tel:${passenger.phone}`);
    }
  };

  const handleEmail = () => {
    if (passenger.email) {
      window.open(`mailto:${passenger.email}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 pt-12 pb-6 px-6">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/passengers')}
            className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
          >
            <i className="ri-arrow-right-line text-white text-lg"></i>
          </button>
          
          <h1 className="text-xl font-bold text-white font-arabic">الملف الشخصي</h1>
          
          <button className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-more-line text-white text-lg"></i>
          </button>
        </div>

        {/* Profile Header */}
        <div className="text-center">
          <div className="relative inline-block mb-4">
            <img
              src={passenger.avatar}
              alt={passenger.name}
              className="w-24 h-24 rounded-full object-cover border-4 border-white/20"
            />
            <div className={`absolute bottom-2 right-2 w-6 h-6 rounded-full border-2 border-white ${
              passenger.isOnline ? 'bg-green-500' : 'bg-gray-400'
            }`}></div>
            {passenger.verified && (
              <div className="absolute top-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2 font-arabic">{passenger.name}</h2>
          <p className="text-white/80 mb-2 font-arabic">{passenger.profession}</p>
          
          <div className="flex items-center justify-center space-x-2 space-x-reverse mb-4">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <i key={i} className={`ri-star-${i < Math.floor(passenger.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}></i>
              ))}
            </div>
            <span className="text-white/80 text-sm font-arabic">({passenger.reviewsCount} تقييم)</span>
          </div>

          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
            passenger.isOnline 
              ? 'bg-green-500/20 text-green-100' 
              : 'bg-gray-500/20 text-gray-100'
          } font-arabic`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              passenger.isOnline ? 'bg-green-400' : 'bg-gray-400'
            }`}></div>
            {passenger.isOnline ? 'متصل الآن' : 'غير متصل'}
          </div>
        </div>
      </div>

      {/* Flight Info */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <h3 className="font-bold text-gray-800 mb-3 font-arabic">معلومات الرحلة</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-arabic">رقم الرحلة</span>
              <span className="font-bold text-gray-800">{passenger.flightInfo.flightNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-arabic">المقعد</span>
              <span className="font-bold text-gray-800">{passenger.flightInfo.seat}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-arabic">التاريخ والوقت</span>
              <span className="font-bold text-gray-800 font-arabic">{passenger.flightInfo.date} - {passenger.flightInfo.time}</span>
            </div>
            <div className="flex items-center justify-center space-x-2 space-x-reverse pt-2 border-t border-gray-100">
              <span className="text-gray-800 font-arabic">{passenger.flightInfo.departure}</span>
              <i className="ri-arrow-left-line text-blue-500"></i>
              <span className="text-gray-800 font-arabic">{passenger.flightInfo.arrival}</span>
            </div>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="px-6 mb-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-3 font-arabic">نبذة شخصية</h3>
          <p className="text-gray-600 mb-4 font-arabic">{passenger.bio}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <span className="text-gray-500 text-sm font-arabic">العمر</span>
              <p className="font-bold text-gray-800">{passenger.age} سنة</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm font-arabic">الجنسية</span>
              <p className="font-bold text-gray-800 font-arabic">{passenger.country}</p>
            </div>
          </div>

          <div className="mb-4">
            <span className="text-gray-500 text-sm font-arabic">اللغات</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {passenger.languages.map((language, index) => (
                <span key={index} className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-arabic">
                  {language}
                </span>
              ))}
            </div>
          </div>

          <div>
            <span className="text-gray-500 text-sm font-arabic">الاهتمامات</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {passenger.interests.map((interest, index) => (
                <span key={index} className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm font-arabic">
                  {interest}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Actions */}
      <div className="px-6 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 font-arabic">التواصل</h3>
          
          {!showContactInfo && passenger.showContact && (
            <button
              onClick={() => setShowContactInfo(true)}
              className="w-full bg-blue-500 text-white py-3 rounded-xl font-medium mb-3 font-arabic"
            >
              عرض معلومات التواصل
            </button>
          )}

          {showContactInfo && passenger.showContact && (
            <div className="space-y-3 mb-4">
              <button
                onClick={handleCall}
                className="w-full flex items-center justify-center space-x-3 space-x-reverse bg-green-500 text-white py-3 rounded-xl font-medium"
              >
                <i className="ri-phone-line text-lg"></i>
                <span className="font-arabic">اتصال</span>
              </button>
              
              <button
                onClick={handleEmail}
                className="w-full flex items-center justify-center space-x-3 space-x-reverse bg-gray-500 text-white py-3 rounded-xl font-medium"
              >
                <i className="ri-mail-line text-lg"></i>
                <span className="font-arabic">إيميل</span>
              </button>
            </div>
          )}

          <button
            onClick={handleChatRequest}
            disabled={chatRequested}
            className={`w-full flex items-center justify-center space-x-3 space-x-reverse py-3 rounded-xl font-medium transition-all duration-300 ${
              chatRequested 
                ? 'bg-green-500 text-white' 
                : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg'
            }`}
          >
            <i className={`text-lg ${chatRequested ? 'ri-check-line' : 'ri-chat-3-line'}`}></i>
            <span className="font-arabic">
              {chatRequested ? 'تم إرسال الطلب' : 'إرسال طلب محادثة'}
            </span>
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3">
        <div className="grid grid-cols-4 gap-1">
          <button 
            onClick={() => navigate('/main')}
            className="flex flex-col items-center py-2 text-gray-400"
          >
            <i className="ri-home-line text-xl mb-1"></i>
            <span className="text-xs font-arabic">الرئيسية</span>
          </button>
          <button 
            onClick={() => navigate('/passengers')}
            className="flex flex-col items-center py-2 text-blue-500"
          >
            <i className="ri-group-line text-xl mb-1"></i>
            <span className="text-xs font-arabic">الركاب</span>
          </button>
          <button 
            onClick={() => navigate('/chats')}
            className="flex flex-col items-center py-2 text-gray-400"
          >
            <i className="ri-chat-3-line text-xl mb-1"></i>
            <span className="text-xs font-arabic">الرسائل</span>
          </button>
          <button 
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center py-2 text-gray-400"
          >
            <i className="ri-user-line text-xl mb-1"></i>
            <span className="text-xs font-arabic">الملف</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        .font-arabic {
          font-family: 'Tajawal', 'Noto Sans Arabic', sans-serif;
        }
      `}</style>
    </div>
  );
}